import { useMutation, useQueryClient } from "@tanstack/react-query";

import { axiosInstance } from "../../lib/axios";
import { INTEGRATION_SCOPES } from "../constants";

import { CONNECTED_INTEGRATIONS_QUERY_KEY } from "./useFetchConnectedIntegration";
import { CRAWLER_PREFERENCE_QUERY_KEY } from "./useFetchCrawlerPreference";

import type { UpdateCrawlerPreferenceRequest } from "../types/crawlerPreference";

// Hook to update crawler preference data
export const useUpdateCrawlerPreference = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      source,
      verifiableId,
      data,
      integrationScope = INTEGRATION_SCOPES.USER,
      organizationUuid,
    }: UpdateCrawlerPreferenceRequest) => {
      return await axiosInstance.post("/preference/crawler", {
        source,
        verifiableId,
        data,
        integration_scope: integrationScope,
        organization_uuid: organizationUuid,
      });
    },
    onSuccess: (response, variables) => {
      const integrationScope =
        variables.integrationScope ?? INTEGRATION_SCOPES.USER;

      /*
      An organization row is never readable on its own — its selection comes back nested
      under the deployment parent, so that is the query which just went stale.
      */
      if (integrationScope === INTEGRATION_SCOPES.ORGANIZATION) {
        queryClient.invalidateQueries({
          queryKey: CONNECTED_INTEGRATIONS_QUERY_KEY(
            variables.source,
            INTEGRATION_SCOPES.DEPLOYMENT,
          ),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: CRAWLER_PREFERENCE_QUERY_KEY(
            variables.source,
            variables.verifiableId,
            integrationScope,
          ),
        });
      }

      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};
